import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes, faSpinner, faClock, faCalendarAlt } from '@fortawesome/free-solid-svg-icons';
import './EditCampaignModal.css';

/**
 * AutoPacingScheduleModal - Modal for creating/editing auto pacing schedules
 * 
 * Features:
 * - Schedule type selection (daily, hourly, weekly, custom)
 * - Execution time configuration
 * - Budget guardrails (min/max budget, max change %)
 * - Custom optimization prompt
 * - Timezone selection
 */
const AutoPacingScheduleModal = ({ 
  isOpen, 
  onClose, 
  campaign, 
  schedule = null, // null for create, object for edit
  onSubmit, 
  isSubmitting 
}) => {
  // Form state
  const [name, setName] = useState('');
  const [scheduleType, setScheduleType] = useState('daily');
  const [weeklyDay, setWeeklyDay] = useState(1); // 0=Sunday, 1=Monday, etc.
  const [executionTime, setExecutionTime] = useState('09:00');
  const [timezone, setTimezone] = useState('America/Los_Angeles');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [customPrompt, setCustomPrompt] = useState('');
  const [maxBudgetChangePercent, setMaxBudgetChangePercent] = useState(50);
  const [minDailyBudget, setMinDailyBudget] = useState('');
  const [maxDailyBudget, setMaxDailyBudget] = useState('');
  const [cronExpression, setCronExpression] = useState('');

  // Populate form when editing
  useEffect(() => {
    if (schedule) {
      setName(schedule.name || '');
      setScheduleType(schedule.scheduleType || 'daily');
      setWeeklyDay(schedule.weeklyDay ?? 1);
      setExecutionTime(schedule.executionTime?.substring(0, 5) || '09:00');
      setTimezone(schedule.timezone || 'America/Los_Angeles');
      setStartDate(schedule.startDate?.split('T')[0] || '');
      setEndDate(schedule.endDate?.split('T')[0] || '');
      setCustomPrompt(schedule.customPrompt || '');
      setMaxBudgetChangePercent(schedule.maxBudgetChangePercent || 50);
      setMinDailyBudget(schedule.minDailyBudget || '');
      setMaxDailyBudget(schedule.maxDailyBudget || '');
      setCronExpression(schedule.cronExpression || '');
    } else {
      // Reset form for new schedule
      setName(`Auto Pacing - ${campaign?.name || 'Campaign'}`);
      setScheduleType('daily');
      setWeeklyDay(1); // Default to Monday
      setExecutionTime('09:00');
      setTimezone('America/Los_Angeles');
      setStartDate('');
      setEndDate('');
      setCustomPrompt('');
      setMaxBudgetChangePercent(50);
      setMinDailyBudget('');
      setMaxDailyBudget('');
      setCronExpression('');
    }
  }, [schedule, campaign, isOpen]);

  const handleSubmit = (e) => {
    e.preventDefault();

    const scheduleData = {
      name: name.trim(),
      scheduleType,
      weeklyDay: scheduleType === 'weekly' ? parseInt(weeklyDay) : null,
      executionTime: `${executionTime}:00`,
      timezone,
      startDate: startDate || null,
      endDate: endDate || null,
      customPrompt: customPrompt.trim() || null,
      maxBudgetChangePercent: parseFloat(maxBudgetChangePercent) || 50,
      minDailyBudget: minDailyBudget ? parseFloat(minDailyBudget) : null,
      maxDailyBudget: maxDailyBudget ? parseFloat(maxDailyBudget) : null,
      cronExpression: scheduleType === 'custom' ? cronExpression : null
    };

    onSubmit(scheduleData);
  };

  const isFormValid = () => {
    if (!name.trim()) return false;
    if (scheduleType === 'custom' && !cronExpression.trim()) return false;
    if (minDailyBudget && maxDailyBudget && parseFloat(minDailyBudget) > parseFloat(maxDailyBudget)) return false;
    return true;
  };

  // Get today's date in YYYY-MM-DD format for min date validation (using local time, not UTC)
  const today = (() => {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  })();

  if (!isOpen) return null;

  const timezones = [
    { value: 'America/Los_Angeles', label: 'Pacific Time (PT)' },
    { value: 'America/Denver', label: 'Mountain Time (MT)' },
    { value: 'America/Chicago', label: 'Central Time (CT)' },
    { value: 'America/New_York', label: 'Eastern Time (ET)' },
    { value: 'UTC', label: 'UTC' },
    { value: 'Europe/London', label: 'London (GMT/BST)' },
    { value: 'Europe/Paris', label: 'Paris (CET/CEST)' },
    { value: 'Asia/Tokyo', label: 'Tokyo (JST)' },
    { value: 'Asia/Singapore', label: 'Singapore (SGT)' },
    { value: 'Australia/Sydney', label: 'Sydney (AEST/AEDT)' }
  ];

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content edit-campaign-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>
            <FontAwesomeIcon icon={faClock} style={{ marginRight: '8px' }} />
            {schedule ? 'Edit Auto Pacing Schedule' : 'Create Auto Pacing Schedule'}
          </h2>
          <button className="close-button" onClick={onClose}>
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="modal-body">
            {/* Campaign Info */}
            <div className="campaign-info" style={{ marginBottom: '16px', padding: '12px', background: '#f8f9fa', borderRadius: '8px' }}>
              <strong>{campaign?.name}</strong>
              <span style={{ marginLeft: '8px', color: '#666' }}>
                {campaign?.destination?.toUpperCase()}
              </span>
            </div>

            {/* Schedule Name */}
            <div className="form-group">
              <label htmlFor="name">Schedule Name *</label>
              <input
                type="text"
                id="name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="form-control"
                placeholder="Enter schedule name"
                required
              />
            </div>

            {/* Schedule Type */}
            <div className="form-group">
              <label htmlFor="scheduleType">Schedule Type</label>
              <select
                id="scheduleType"
                value={scheduleType}
                onChange={(e) => setScheduleType(e.target.value)}
                className="form-control"
              >
                <option value="daily">Daily</option>
                <option value="hourly">Hourly</option>
                <option value="weekly">Weekly</option>
                <option value="custom">Custom (Cron)</option>
              </select>
            </div>

            {/* Weekly Day Selector */}
            {scheduleType === 'weekly' && (
              <div className="form-group">
                <label htmlFor="weeklyDay">Day of Week</label>
                <select
                  id="weeklyDay"
                  value={weeklyDay}
                  onChange={(e) => setWeeklyDay(e.target.value)}
                  className="form-control"
                >
                  <option value={0}>Sunday</option>
                  <option value={1}>Monday</option>
                  <option value={2}>Tuesday</option>
                  <option value={3}>Wednesday</option>
                  <option value={4}>Thursday</option>
                  <option value={5}>Friday</option>
                  <option value={6}>Saturday</option>
                </select>
              </div>
            )}

            {/* Custom Cron Expression */}
            {scheduleType === 'custom' && (
              <div className="form-group">
                <label htmlFor="cronExpression">Cron Expression *</label>
                <input
                  type="text"
                  id="cronExpression"
                  value={cronExpression}
                  onChange={(e) => setCronExpression(e.target.value)}
                  className="form-control"
                  placeholder="e.g., 0 9 * * 1-5 (9 AM weekdays)"
                  required
                />
                <small style={{ color: '#666' }}>
                  Format: minute hour day-of-month month day-of-week
                </small>
              </div>
            )}

            {/* Execution Time (for non-hourly) */}
            {scheduleType !== 'hourly' && scheduleType !== 'custom' && (
              <div className="form-group">
                <label htmlFor="executionTime">Execution Time</label>
                <input
                  type="time"
                  id="executionTime"
                  value={executionTime}
                  onChange={(e) => setExecutionTime(e.target.value)}
                  className="form-control"
                />
              </div>
            )}

            {/* Timezone */}
            <div className="form-group">
              <label htmlFor="timezone">Timezone</label>
              <select
                id="timezone"
                value={timezone}
                onChange={(e) => setTimezone(e.target.value)}
                className="form-control"
              >
                {timezones.map(tz => (
                  <option key={tz.value} value={tz.value}>{tz.label}</option>
                ))}
              </select>
            </div>

            {/* Date Range */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
              <div className="form-group">
                <label htmlFor="startDate">
                  <FontAwesomeIcon icon={faCalendarAlt} style={{ marginRight: '4px' }} />
                  Start Date (Optional)
                </label>
                <input
                  type="date"
                  id="startDate"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  className="form-control"
                  min={today}
                />
              </div>
              <div className="form-group">
                <label htmlFor="endDate">End Date (Optional)</label>
                <input
                  type="date"
                  id="endDate"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  className="form-control"
                  min={startDate || today}
                />
              </div>
            </div>

            {/* Budget Guardrails Section */}
            <div style={{ marginTop: '16px', padding: '16px', background: '#f8f9fa', borderRadius: '8px' }}>
              <h4 style={{ margin: '0 0 12px 0', fontSize: '14px', fontWeight: '600' }}>
                Budget Guardrails
              </h4>

              <div className="form-group">
                <label htmlFor="maxBudgetChangePercent">Max Budget Change (%)</label>
                <input
                  type="number"
                  id="maxBudgetChangePercent"
                  value={maxBudgetChangePercent}
                  onChange={(e) => setMaxBudgetChangePercent(e.target.value)}
                  className="form-control"
                  min="1"
                  max="100"
                  placeholder="50"
                />
                <small style={{ color: '#666' }}>
                  Maximum percentage the budget can change in a single execution
                </small>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                <div className="form-group">
                  <label htmlFor="minDailyBudget">Min Daily Budget ($)</label>
                  <input
                    type="number"
                    id="minDailyBudget"
                    value={minDailyBudget}
                    onChange={(e) => setMinDailyBudget(e.target.value)}
                    className="form-control"
                    min="0"
                    step="0.01"
                    placeholder="Optional"
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="maxDailyBudget">Max Daily Budget ($)</label>
                  <input
                    type="number"
                    id="maxDailyBudget"
                    value={maxDailyBudget}
                    onChange={(e) => setMaxDailyBudget(e.target.value)}
                    className="form-control"
                    min="0"
                    step="0.01"
                    placeholder="Optional"
                  />
                </div>
              </div>
            </div>

            {/* Custom Prompt */}
            <div className="form-group" style={{ marginTop: '16px' }}>
              <label htmlFor="customPrompt">Custom Optimization Focus (Optional)</label>
              <textarea
                id="customPrompt"
                value={customPrompt}
                onChange={(e) => setCustomPrompt(e.target.value)}
                className="form-control"
                rows="3"
                placeholder="e.g., Focus on maximizing conversions while maintaining CPA under $50"
              />
              <small style={{ color: '#666' }}>
                Additional instructions for the AI optimizer
              </small>
            </div>
          </div>

          <div className="modal-footer">
            <button
              type="button"
              className="btn btn-secondary"
              onClick={onClose}
              disabled={isSubmitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={!isFormValid() || isSubmitting}
            >
              {isSubmitting ? (
                <>
                  <FontAwesomeIcon icon={faSpinner} spin />
                  {' '}{schedule ? 'Updating...' : 'Creating...'}
                </>
              ) : (
                schedule ? 'Update Schedule' : 'Create Schedule'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AutoPacingScheduleModal;

